import React, { useCallback, useEffect, useState, forwardRef, useRef, useMemo } from "react";

import {
  Button,
  DrawerBody,
  DrawerHeader,
  DrawerHeaderTitle,
  Persona,
  Textarea,
  Popover,
  PopoverSurface,
  PopoverTrigger,
  Spinner,
  Subtitle1,
  makeStyles,
  mergeClasses
} from "@fluentui/react-components";

import { SendFilled, AttachRegular, Emoji24Regular, CubeMultipleRegular } from "@fluentui/react-icons";
import { MAX_MESSAGE_LENGTH, formatMessageBody } from "../../utils/chat-message";
import { useMaintainScrollPosition } from "../misc/useMaintainScrollPosition";
import { SoundBarAnimation } from "../room/ChatSidebar";
import { EmojiPicker } from "../room/EmojiPicker";
import { uploadV2 } from "../../utils/media-utils";
import { proxiedUrlFor } from "../../utils/media-url-utils";
import { spawnChatMessage } from "../chat-message";
import { PermissionNotification } from "../room/PermissionNotifications";
import { useRoomPermissions } from "../room/useRoomPermissions";
import { useRole } from "../room/useRole";
import PropTypes from "prop-types";
import { CloseButton } from "../input/CloseButton";

const useStyles = makeStyles({
  headerDrawer: {
    display: " flex",
    gap: " 8px",
    alignSelf: " stretch",
    backgroundColor: "#16181E"
  },
  drawerBody: {
    paddingTop: "20px"
  },
  sectionHeader: {
    display: " flex",
    padding: " 8px 0px",
    flexDirection: " column",
    alignItems: " flex-start",
    gap: "var(--Font-size-100, 10px)",
    alignSelf: "stretch"
  },
  sectionHeaderText: {
    color: "var(--Gray-3, #828282)"
  },
  sectionContent: {
    width: "100%",
    display: " flex",
    padding: " 4px",
    alignItems: " center",
    height: "48px",
    borderRadius: "4px",
    justifyContent: "space-between",
    gap: " 8px",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.1)"
    }
  },
  persona: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    // height: "36px",
    "& .fui-Avatar__image": {
      // width: "36px",
      // height: "36px",
      // minWidth: "36px"
    },
    "& .fui-Avatar": {
      // height: "36px",
      // minWidth: "36px",
      // width: "36px",
    },
    "& .fui-Persona": {
      display: "flex",
      justifyContent: "center",
      alignItems: "center"
      // height: "36px"
    },
    "& span": {
      fontSize: "var(--Font-size-500, 20px)",
      fontStyle: "normal",
      fontWeight: "600",
      lineHeight: "var(--Line-height-500, 28px)"
    }
  },
  icons: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    "& > *": {
      cursor: "pointer"
    }
  },
  divider: {
    padding: "5px 0px 4px 0px"
  },
  contentParticipants: {},
  footerParticipants: {
    display: "flex",
    padding: "16px var(--Font-size-600, 24px) var(--Font-size-600, 24px) var(--Font-size-600, 24px)",
    justifyContent: "flex-end",
    width: "100%",
    backgroundColor: "#16181E"
  },
  redIcon: {
    color: "var(--Red, #EB5757)"
  },
  messageListContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "15px"
  },
  messageBubble: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    gap: "5px",
    paddingLeft: "40px",
    "& .message-author-container": {
      justifyContent: "flex-end",
      alignItems: "center",
      gap: "10px",
      display: "inline-flex"
    },
    "& .message-bubble-content": {
      display: "flex",
      padding: "20px 15px",
      justifyContent: "center",
      alignItems: "center",
      gap: "10px",
      alignSelf: "stretch",
      borderRadius: "20px 0px 20px 20px",
      background: "var(--Dark, #3A4048)",
      maxWidth: "280px",
      marginLeft: "auto"
    },
    "& .message-bubble-text": {
      color: "var(--White, #FFF)",
      textAlign: "left"
    },
    "& .spawn-message-container": {
      display: "flex",
      alignItems: "flex-end"
    }
  },
  messageBubbleUser: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "5px",
    "& .message-author-container": {
      justifyContent: "flex-start",
      alignItems: "center",
      gap: "10px",
      display: "inline-flex",
      flexDirection: "row-reverse"
    },
    "& .message-bubble-content": {
      display: "flex",
      padding: "20px 15px",
      justifyContent: "center",
      alignItems: "center",
      gap: "10px",
      alignSelf: "stretch",
      borderRadius: "0px 20px 20px 20px",
      background: "#181A20",
      maxWidth: "280px",
      marginRight: "auto"
    },
    "& .spawn-message-container": {
      flexDirection: "row-reverse",
      display: "flex",
      alignItems: "flex-end"
    },
    "& .message-bubble-text": {
      color: "var(--White, #FFF)",
      textAlign: "left"
    }
  },
  messageAuthorDate: {
    textAlign: "right",
    color: "#BDBDBD",
    fontSize: 14,
    fontFamily: "Segoe UI",
    fontWeight: "400"
  },
  messageAuthor: { textAlign: "right", color: "white", fontSize: 14, fontFamily: "Segoe UI", fontWeight: "700" },
  messageBubbleText: {
    color: "var(--White, #FFF)",
    textAlign: "right"
  },
  sendStyles: {
    backgroundColor: "#27AE60",
    "&:hover": {
      backgroundColor: "#27AE60"
    },
    minWidth: "32px",
    width: "32px",
    minHeight: "32px",
    height: "32px",
    margin: "auto 0"
  },
  sendStylesDisabled: {
    backgroundColor: "#F0F0F0",
    minWidth: "32px",
    width: "32px",
    minHeight: "32px",
    height: "32px",
    margin: "auto 0"
  },
  textareaInput: {
    "& > :first-child": {
      resize: "none",
      minHeight: "32px"
    }
  },
  sendMessageContainer: {
    display: "flex",
    gap: "8px",
    width: "100%",
    justifyContent: "space-between"
  },
  inputIcon: {
    minWidth: "20px",
    width: "20px"
  },
  chatSoundbar: {
    display: "flex",
    alignItems: "center",
    marginLeft: "12px",
    marginRight: "12px"
  },
  chatMessageList: {
    display: "flex",
    flexDirection: "column",
    gap: "15px",
    paddingBottom: "60px"
  },
  chatMessageBubble: {
    wordBreak: "break-word",
    "& p": {
      margin: "0 0 0.5em 0",
      "&:last-child": {
        marginBottom: 0
      }
    },
    "& strong": {
      fontWeight: "bold",
      fontSize: "inherit"
    },
    "& em": {
      fontStyle: "italic"
    },
    "& h1, & h2, & h3, & h4, & h5, & h6": {
      marginBottom: "0.5em",
      lineHeight: "normal"
    },
    "& ul, & ol": {
      margin: "0.5em 0 0.5em 1.5em",
      paddingLeft: "1.5em"
    },
    "& ul": {
      listStyleType: "disc"
    },
    "& ol": {
      listStyleType: "decimal"
    },
    "& li": {
      marginBottom: "0.25em",
      lineHeight: 1.4
    },
    "& code": {
      fontFamily: "monospace",
      backgroundColor: "rgba(255, 255, 255, 0.1)",
      padding: "0.1em 0.3em",
      borderRadius: "3px",
      fontSize: "0.9em",
      wordBreak: "break-all"
    },
    "& pre": {
      fontFamily: "monospace",
      backgroundColor: "rgba(0, 0, 0, 0.2)",
      padding: "1em",
      borderRadius: "4px",
      overflowX: "auto",
      margin: "0.5em 0",
      fontSize: "0.9em",
      wordBreak: "break-all",
      whiteSpace: "pre-wrap"
    },
    "& a": {
      textDecoration: "underline"
    },
    "& .chat-emoji": {
      verticalAlign: "middle"
    }
  },
  dotFlashing: {
    position: "relative",
    width: "8px",
    height: "8px",
    borderRadius: "8px",
    backgroundColor: "#fff",
    color: "#fff",
    animation: "dotFlashing 1s infinite linear alternate",
    animationDelay: "0.5s",
    marginLeft: "10px",
    marginRight: "10px",
    "&::before": {
      content: '""',
      display: "inline-block",
      position: "absolute",
      top: "0",
      left: "-12.5px",
      width: "8px",
      height: "8px",
      borderRadius: "8px",
      backgroundColor: "#fff",
      color: "#fff",
      animation: "dotFlashing 1s infinite alternate",
      animationDelay: "0s"
    },
    "&::after": {
      content: '""',
      display: "inline-block",
      position: "absolute",
      top: "0",
      left: "12.5px",
      width: "8px",
      height: "8px",
      borderRadius: "8px",
      backgroundColor: "#fff",
      color: "#fff",
      animation: "dotFlashing 1s infinite alternate",
      animationDelay: "1s"
    }
  }
});

export default function FluentChatOverlay({
  hubChannel,
  toggleChat,
  selectedUser,
  messageGroups,
  onSendMessage,
  onUploadAttachments,
  onKeyDown,
  message,
  setMessage,
  isCommand,
  isDisabled,
  onSpawn,
  inputRef,
  onSelectEmoji,
  canTextChat
}) {
  const styles = useStyles();

  const [onScrollList, listRef, scrolledToBottom] = useMaintainScrollPosition(messageGroups);

  const canSpawnMessages = hubChannel.can("spawn_and_move_media");
  const isMobile = AFRAME.utils.device.isMobile();

  const onSpawnMessage = useCallback(() => {
    spawnChatMessage(message);
    setMessage("");
  }, [message, setMessage]);

  const { text_chat: textChatEnabled } = useRoomPermissions();

  const isMod = useRole("owner");

  const inputContentAfter = useMemo(() => {
    return (
      <div style={{ display: "flex", gap: "4px" }}>
        {!isMobile && <EmojiPickerPopoverButton onSelectEmoji={onSelectEmoji} />}
        {message.length === 0 && canSpawnMessages ? <MessageAttachmentButton onChange={onUploadAttachments} /> : null}
        {canSpawnMessages && (
          <Button
            className={styles.inputIcon}
            appearance="transparent"
            disabled={isDisabled}
            onClick={onSpawnMessage}
            icon={<CubeMultipleRegular />}
          />
        )}
      </div>
    );
  }, [
    isMobile,
    onSelectEmoji,
    message,
    canSpawnMessages,
    onUploadAttachments,
    isDisabled,
    onSpawnMessage,
    styles.inputIcon
  ]);

  return (
    <>
      <DrawerHeader className={styles.headerDrawer}>
        <DrawerHeaderTitle style={{ width: "100%" }} action={<CloseButton onClick={toggleChat} />}>
          {selectedUser && (
            <Persona
              size="large"
              className={styles.persona}
              name={`${selectedUser?.profile.displayName}`}
              avatar={{
                image: {
                  src: selectedUser?.profile?.thumbnail
                }
              }}
            />
          )}
          {!selectedUser && <Subtitle1>Everyone</Subtitle1>}
        </DrawerHeaderTitle>
      </DrawerHeader>

      <DrawerBody className={styles.drawerBody}>
        <ChatMessageList className={styles.chatMessageList} ref={listRef} onScroll={onScrollList}>
          {messageGroups.map(entry => {
            const { id, systemMessage, type } = entry;
            if (systemMessage) {
              // return <SystemMessage key={id} {...entry} />;
            } else {
              if (type === "permission") {
                // return <PermissionMessageGroup key={id} {...entry} />;
              } else {
                return (
                  <ChatMessageGroup
                    {...entry}
                    onSpawn={onSpawn}
                    canSpawnMessages={canSpawnMessages}
                    key={`chat-message-group-${id}`}
                  />
                );
              }
            }
          })}
        </ChatMessageList>
        {/* {!canTextChat && <PermissionNotification permission={"text_chat"} />} */}
        {!textChatEnabled && isMod && <PermissionNotification permission={"text_chat"} isMod={true} />}
      </DrawerBody>

      <div className={styles.footerParticipants}>
        {canTextChat && (
          <div className={styles.sendMessageContainer}>
            <div style={{ display: "flex", alignItems: "center", flex: 1 }}>
              <Textarea
                ref={inputRef}
                onChange={e => setMessage(e.target.value)}
                value={message}
                onKeyDown={onKeyDown}
                maxLength={MAX_MESSAGE_LENGTH}
                appearance="filled-ligther"
                placeholder="Type message here"
                className={styles.textareaInput}
                style={{ display: "flex", flex: 1 }}
              />
              {inputContentAfter}
            </div>
            <Button
              className={isDisabled && !isCommand ? styles.sendStylesDisabled : styles.sendStyles}
              onClick={onSendMessage}
              disabled={isDisabled && !isCommand}
              icon={<SendFilled />}
            />
          </div>
        )}
        {!canTextChat && <PermissionNotification permission={"text_chat"} />}
      </div>
    </>
  );
}

export const ChatMessageGroup = React.memo(function ChatMessageGroup({ messages, ...commonProps }) {
  return (
    <>
      {messages.map(message => (
        <MemoizedMessageComponent
          message={message}
          key={`chat-message-${message.id}`} // Use stable message ID
          canSpawnMessages={commonProps.canSpawnMessages}
          sender={commonProps.sender}
          timestamp={commonProps.timestamp}
          sent={commonProps.sent}
          onSpawn={commonProps.onSpawn}
          loading={commonProps.loading}
          playAudio={commonProps.playAudio}
          cancelAllPlay={commonProps.cancelAllPlay}
        />
      ))}
    </>
  );
});

function ChatMessage({
  canSpawnMessages,
  message,
  isMe,
  children,
  timestamp,
  sender,
  onSpawn,
  loading,
  forceSpawn,
  actionChildren = <></>,
  spawnMessageStyles = {}
}) {
  const styles = useStyles();

  const formatTime = timestamp => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: true });
  };

  return (
    <div className={isMe ? styles.messageBubble : styles.messageBubbleUser}>
      <div className={"message-author-container"}>
        <span className={styles.messageAuthorDate}>{formatTime(timestamp)}</span>
        <span className={styles.messageAuthor}>{isMe ? "You" : sender}</span>
      </div>
      <div className="spawn-message-container" style={spawnMessageStyles}>
        {(message?.body?.canSpawn || forceSpawn) && canSpawnMessages && (
          <Button appearance="transparent" icon={<CubeMultipleRegular />} onClick={() => onSpawn(message?.body?.src)} />
        )}
        {actionChildren}
        <div className={"message-bubble-content"}>
          {!loading && <div className={mergeClasses("message-bubble-text", styles.chatMessageBubble)}>{children}</div>}
          {loading && (
            <div className="snippet" data-title="dot-flashing">
              <div className="stage">
                <div className={styles.dotFlashing}></div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

ChatMessageGroup.propTypes = {
  messages: PropTypes.array.isRequired,
  canSpawnMessages: PropTypes.bool.isRequired,
  sender: PropTypes.string.isRequired,
  timestamp: PropTypes.number.isRequired,
  sent: PropTypes.bool.isRequired,
  onSpawn: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  playAudio: PropTypes.func,
  cancelAllPlay: PropTypes.func
};

export const ChatMessageList = forwardRef(({ children, ...rest }, ref) => (
  <ul {...rest} ref={ref}>
    {children}
  </ul>
));

ChatMessageList.displayName = "ChatMessageList";

const onSpawnMessageInList = () => {};

const isImage = url => {
  if (typeof url !== "string") return false;

  // Check if the URL has an image file extension (quick check)
  const imageExtensions = ["png", "jpg", "jpeg", "gif", "bmp", "webp"];
  const urlExtension = url.split(".").pop().toLowerCase();

  if (imageExtensions.includes(urlExtension)) {
    return true;
  }

  return false;
};

function MessageComponent({ message, ...additionalProps }) {
  const styles = useStyles();
  const { canSpawnMessages, sender, timestamp, sent: isMe, onSpawn, loading, key } = additionalProps;

  const bodyContent = message?.body?.message !== undefined ? message.body : message;
  const bodyMessage = bodyContent.message || message.body;

  if (loading) {
    return <ChatMessage isMe={isMe} loading={loading} sender={sender} timestamp={timestamp} />;
  }

  //Handle Images From AI
  if (bodyMessage && typeof bodyMessage === "string" && bodyMessage?.startsWith("https") && isImage(bodyMessage)) {
    return (
      <ChatMessage canSpawnMessages={canSpawnMessages} sender={sender} timestamp={timestamp} isMe={isMe}>
        <div className={styles.imageAi}>
          <img src={proxiedUrlFor(message.body)}></img>
          {/* <a href="#" onClick={sendToScene}>Open in Metaverse</a> */}
        </div>
      </ChatMessage>
    );
  }

  switch (message.type) {
    case "private-chat": {
      const { formattedBody } = formatMessageBody(message.body);
      const { cancelAllPlay, playAudio } = additionalProps || {};

      let actionChildren = null;
      let spawnMessageStyles = undefined;

      if ((!message.audio || message.loadingAudio) && message.playable && sender !== "You") {
        // Render loading state with Spinner
        actionChildren = (
          <div className={styles.chatSoundbar}>
            <Spinner />
          </div>
        );
        spawnMessageStyles = { alignItems: "center" };
      } else if (message.audio != null && !message.play) {
        actionChildren = (
          <SoundBarAnimation onClick={() => playAudio(message)} containerClassName={styles.chatSoundbar} play={false} />
        );
        spawnMessageStyles = { alignItems: "center" };
      } else if (message.play) {
        actionChildren = (
          <SoundBarAnimation play={message.play} onClick={cancelAllPlay} containerClassName={styles.chatSoundbar} />
        );
        spawnMessageStyles = { alignItems: "center" };
      }

      return (
        <ChatMessage
          key={key}
          canSpawnMessages={canSpawnMessages}
          sender={sender}
          timestamp={timestamp}
          isMe={isMe}
          spawnMessageStyles={spawnMessageStyles}
          actionChildren={actionChildren}
        >
          {formattedBody}
        </ChatMessage>
      );
    }
    case "chat": {
      const { formattedBody, monospace, emoji } = formatMessageBody(message.body);
      return (
        <ChatMessage
          key={key}
          forceSpawn={true}
          onSpawn={() => spawnChatMessage(message.body)}
          canSpawnMessages={canSpawnMessages}
          sender={sender}
          timestamp={timestamp}
          isMe={isMe}
        >
          {formattedBody}
        </ChatMessage>
      );
    }
    case "video":
      return (
        <ChatMessage
          key={key}
          onSpawn={onSpawn}
          canSpawnMessages={canSpawnMessages}
          message={message}
          media
          sender={additionalProps.sender}
          timestamp={additionalProps.timestamp}
          isMe={additionalProps?.sent}
        >
          <video controls src={message.body.src} />
        </ChatMessage>
      );
    case "image":
    case "photo":
      return (
        <ChatMessage
          key={key}
          onSpawn={onSpawn}
          canSpawnMessages={canSpawnMessages}
          message={message}
          media
          sender={additionalProps.sender}
          timestamp={additionalProps.timestamp}
          isMe={additionalProps?.sent}
        >
          <img src={message.body.src} />
        </ChatMessage>
      );
    case "audio":
      return (
        <ChatMessage
          key={key}
          onSpawn={onSpawn}
          canSpawnMessages={canSpawnMessages}
          message={message}
          media
          sender={additionalProps.sender}
          timestamp={additionalProps.timestamp}
          isMe={additionalProps?.sent}
        >
          <audio style={{ maxWidth: "250px" }} controls src={message.body.src} />
        </ChatMessage>
      );
    case "permission":
      return (
        <ChatMessage
          key={key}
          onSpawn={onSpawn}
          canSpawnMessages={canSpawnMessages}
          message={message}
          media
          sender={additionalProps.sender}
          timestamp={additionalProps.timestamp}
          isMe={additionalProps?.sent}
        >
          <img src={message.body.src} />
        </ChatMessage>
      );
    case "object": {
      if (message.body?.src || message?.body?.name) {
        const url = message?.body?.src ? new URL(message?.body?.src) : "";
        const urlFilename = url?.pathname?.split("/")?.pop();
        const { formattedBody } = formatMessageBody(message?.body?.name || urlFilename);
        return (
          <ChatMessage
            key={key}
            onSpawn={onSpawn}
            canSpawnMessages={canSpawnMessages}
            message={message}
            media
            sender={additionalProps.sender}
            timestamp={additionalProps.timestamp}
            isMe={additionalProps?.sent}
          >
            <p style={{ fontStyle: "italic" }}>{formattedBody}</p>
          </ChatMessage>
        );
      }
    }
    // eslint-disable-next-line no-fallthrough
    default:
      return null;
  }
}

MessageComponent.propTypes = {
  message: PropTypes.object.isRequired
};

const MemoizedMessageComponent = React.memo(MessageComponent);

export const EmojiPickerPopoverButton = React.memo(function EmojiPickerPopoverButton({ onSelectEmoji }) {
  const styles = useStyles();
  // We're using a ref here, since we don't want to re-render anything, but we
  // do want to know if the Shift key is down when an emoji is selected.
  const shiftKeyDown = useRef(false);

  const [open, setOpen] = useState(false);

  const handleOpenChange = (_, data) => {
    setOpen(data.open);
  };

  useEffect(() => {
    const onKeyDown = e => {
      if (e.key === "Shift") shiftKeyDown.current = true;
    };
    const onKeyUp = e => {
      if (e.key === "Shift") shiftKeyDown.current = false;
    };

    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);

    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
    };
  }, []);

  return (
    <div position="center">
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger>
          <Button className={styles.inputIcon} appearance="transparent" icon={<Emoji24Regular />} />
        </PopoverTrigger>
        <PopoverSurface>
          <EmojiPicker
            onSelect={emoji => {
              const keepPickerOpen = shiftKeyDown.current;
              onSelectEmoji({ emoji, pickerRemainedOpen: keepPickerOpen });
              if (!keepPickerOpen) {
                setOpen(false);
              }
            }}
          />
        </PopoverSurface>
      </Popover>
    </div>
  );
});

EmojiPickerPopoverButton.propTypes = {
  onSelectEmoji: PropTypes.func.isRequired
};

export function MessageAttachmentButton(props) {
  const styles = useStyles();

  const fileInputRef = useRef(null);

  const handleButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <>
      <Button
        as="label"
        className={styles.inputIcon}
        appearance="transparent"
        icon={<AttachRegular />}
        onClick={handleButtonClick}
      />
      <input type="file" style={{ display: "none" }} disabled={props.disabled} ref={fileInputRef} {...props} />
    </>
  );
}

function DalleImageAI({ message, ...props }) {
  const proxiedUrlBody = proxiedUrlFor(message.body);
  const entry = {
    name: proxiedUrlBody,
    path: proxiedUrlBody,
    public_thumbnail: proxiedUrlBody,
    type: "image",
    thumbnail: proxiedUrlBody,
    url: proxiedUrlBody
  };

  async function urlToBlob(url) {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const blob = await response.blob();
    return blob;
  }

  const sendToScene = async () => {
    const file = await urlToBlob(proxiedUrlBody);
    const response = await uploadV2(file, "image/png");

    entry.url = proxiedUrlFor(response.data.url);

    window.APP.scene.emit("action_selected_media_result_entry", { entry, selectAction: "my-media" });
  };

  return (
    <div className={styles.imageAi}>
      <img src={proxiedUrlFor(message.body)}></img>
      <a href="#" onClick={sendToScene}>
        Open in Metaverse
      </a>
    </div>
  );
}
